/**
 * Round 95 — useWorkflowTarget hook.
 *
 * Given a task-carrying response from the backend (Today's Mission,
 * Weekly Plan day, Run My Business daily plan, or Ask PayGBot chat
 * response), returns a stable `{ target, navigateToTarget }` pair.
 *
 * When Round 94's `workflow_target` field is present, it wins. When
 * it's absent (feature flag off, or task couldn't be classified),
 * the hook falls back to the legacy `cta_route` / `cta_label`
 * fields so the pre-Round-94 dashboard behavior is preserved.
 *
 * NOTE: the pure transform functions used to live in
 * `workflowTarget.cjs` so the project's Jest config (CJS, no ESM
 * transform) could unit-test them directly. Webpack's ESM/CJS
 * interop for `.cjs` files proved fragile inside this hook — the
 * default-import destructure sometimes resolved `undefined` at
 * runtime — so the logic now lives inline here. The Jest suite
 * continues to test the identical implementation via
 * `workflowTarget.cjs` (both files must stay in sync).
 */
import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------
// Pure helpers (mirror of workflowTarget.cjs — keep in sync)
// ---------------------------------------------------------------
function buildDeepLinkUrl(target) {
  if (!target || !target.route) return null;
  if (target.version === 'legacy') return target.route;
  const params = new URLSearchParams();
  if (target.asset_id) params.set('asset_id', target.asset_id);
  if (target.task_id) params.set('task_id', target.task_id);
  if (target.return_to) params.set('return_to', target.return_to);
  if (target.mode) params.set('mode', target.mode);
  if (target.asset_type) params.set('asset_type', target.asset_type);
  if (target.execution_kind === 'asset_generator') {
    params.set('auto_generate', '1');
  }
  const qs = params.toString();
  return qs ? `${target.route}?${qs}` : target.route;
}

function legacyFallback(task) {
  if (!task) return null;
  const label = task.cta_label || task.label;
  const route = task.cta_route || task.route;
  if (!label || !route) return null;
  return {
    version: 'legacy',
    route,
    intent: task.topic || task.title || '',
    return_to: null,
    task_id: null,
    task_source: null,
    business_id: null,
    department: null,
    asset_type: null,
    asset_id: null,
    mode: 'create',
    recommended_next_step: null,
    resolution_reason: 'legacy_cta_route',
    cta_label: label,
  };
}

function resolveTarget(task) {
  if (!task) return null;
  const wt = task.workflow_target;
  if (wt && wt.route) {
    return Object.assign({}, wt, {
      cta_label: task.cta_label || wt.intent,
    });
  }
  return legacyFallback(task);
}

// Re-export as named ESM so any downstream consumer can import.
export { resolveTarget, buildDeepLinkUrl, legacyFallback };

/**
 * React hook — memoised resolveTarget + navigation dispatcher.
 */
export default function useWorkflowTarget(task) {
  const navigate = useNavigate();
  const target = useMemo(() => resolveTarget(task), [task]);
  const deepLinkUrl = useMemo(() => buildDeepLinkUrl(target), [target]);
  const navigateToTarget = useCallback(() => {
    if (deepLinkUrl) navigate(deepLinkUrl);
  }, [deepLinkUrl, navigate]);
  return { target, navigateToTarget, deepLinkUrl };
}
